import { NORTH_AMERICAN_COUNTRIES } from '../constants';

/**
 * Converts a camelCase string to snake_case.
 * @param str - The camelCase string to convert.
 * @returns The snake_case version of the input string.
 */
export const camelToSnake = (str: string): string => {
    return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
};

/**
 * Checks if a given URL is an absolute URL.
 *
 * This function is inspired by the backend logic (h.is_absolute_url, https://github.com/SemanticSugar/adroll/blob/f8e071ecdddd0f63813e71f6ca3bf8f0b3b21370/adroll/dotcom/lib/helpers/__init__.py#L376
 *
 * Backslashes are normalized to forward slashes before checking because
 * browsers perform the same normalization when resolving `window.location.href`,
 * so a value like `/\evil.com` navigates to `//evil.com` (i.e. `https://evil.com`).
 * Without normalization, that input would slip past both `new URL()` and the
 * `startsWith('//')` check and be treated as a same-origin relative path.
 *
 * @param url - The URL to check.
 * @returns True if the URL is absolute, false otherwise.
 */
export const isAbsoluteUrl = (url: string): boolean => {
    const normalized = url.replace(/\\/g, '/');

    try {
        new URL(normalized);
        return true;
    } catch {}

    return normalized.startsWith('//');
};

export const isNorthAmericanCountry = (countryCode: string): boolean => {
    return NORTH_AMERICAN_COUNTRIES.includes(countryCode);
};

/**
 * Safe destination to fall back to when a server-provided redirect target turns
 * out to be cross-origin. Matches the backend's `DEFAULT_LOGIN_REDIRECT` in
 * adroll/dotcom/lib/auth/__init__.py so the user lands on the same place they
 * would have if no `next` param had been supplied.
 */
export const SAFE_REDIRECT_FALLBACK = '/home';

/**
 * Resolves a redirect target into a value that is safe to hand to
 * `window.location.href`. If the target is absolute (i.e. would leave this
 * origin once the browser resolves it, including backslash-normalized
 * cross-origin payloads), it is replaced with `SAFE_REDIRECT_FALLBACK`
 * unless its origin appears in `allowedExternalOrigins`.
 *
 * `allowedExternalOrigins` entries must be full origins (scheme + host
 * [+ port]) such as `https://nextroll.okta.com`. The check uses parsed
 * `URL.origin` equality, which is robust against `@`-userinfo tricks and
 * suffix-only payloads like `https://nextroll.okta.com.evil.com`. Protocol-
 * relative payloads (`//host`) are intentionally not matched here because
 * the resolved scheme depends on the current page.
 *
 * Defense-in-depth: the backend `is_absolute_url` should already prevent
 * unexpected absolute URLs from reaching us as redirect targets, but a
 * regression on the server side should not become a free open-redirect for
 * this SPA. See ADFLOWS-3107.
 */
export const resolveSafeRedirect = (
    url: string,
    allowedExternalOrigins: string[] = []
): string => {
    if (!url) {
        // eslint-disable-next-line no-console
        console.error(
            '[security] Rejected empty redirect target, falling back to safe path',
            { url, fallback: SAFE_REDIRECT_FALLBACK }
        );
        return SAFE_REDIRECT_FALLBACK;
    }
    if (isAbsoluteUrl(url)) {
        const normalized = url.replace(/\\/g, '/');
        try {
            const parsed = new URL(normalized);
            if (allowedExternalOrigins.includes(parsed.origin)) {
                // Return the normalized form rather than the original `url`.
                // The origin check ran against `normalized`, so handing the
                // browser the same bytes that were validated closes the
                // validated-vs-used gap (browsers normalize backslashes
                // identically for special schemes, but the discrepancy is
                // exactly the class of bug this guard exists to prevent).
                return normalized;
            }
        } catch {}
        // eslint-disable-next-line no-console
        console.error(
            '[security] Rejected absolute redirect target outside allowlist, falling back to safe path',
            { url, fallback: SAFE_REDIRECT_FALLBACK, allowedExternalOrigins }
        );
        return SAFE_REDIRECT_FALLBACK;
    }
    return url;
};
